import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { CancelTaskButton } from "./CancelTaskButton";
import { DecompositionChecklist } from "./DecompositionChecklist";
import { EscalatedBanner } from "./EscalatedBanner";
import { PendingPlanView } from "./PendingPlanView";
import { MergeApprovalBanner } from "./MergeApprovalBanner";
import { PlanApprovalBanner } from "./PlanApprovalBanner";
import { RerunTaskButton } from "./RerunTaskButton";
import { ResumeTaskButton } from "./ResumeTaskButton";
import { useAuthStore } from "../auth/store";
import { ApiError } from "../api/apiError";
import { useTaskAcceptance } from "../features/acceptance/useTaskAcceptance";
import type { NotificationDTO } from "../features/notifications/types";
import { useEnabledModules } from "../features/modules/useEnabledModules";
import { useNotificationStream } from "../features/notifications/useNotificationStream";
import type { TaskStatusEventResponse } from "../features/queue/api";
import { requestErrorText } from "../features/queue/requestErrorText";
import { isAwaitingMergeApproval } from "../features/mergeApproval/logic";
import { hasUnrecordedPrAttempt } from "../features/queue/unrecordedPrAttempt";
import { useTask } from "../features/queue/useTask";
import { useTaskEvents } from "../features/queue/useTaskEvents";
import { useTaskPlan } from "../features/queue/useTaskPlan";

const HTTP_NOT_FOUND = 404;
const TASK_NOT_FOUND = "task_not_found";

// Экран одной задачи.
//
// Показывает поля Task (статус, описание из issue, ссылку на PR,
// репозиторий, ветку) и read-only лог переходов task_status_event.
//
// Live-обновления — через SSE: useNotificationStream доставляет
// TaskStatusChanged уведомления; этот компонент, получив уведомление
// с совпадающим task_id, инвалидирует react-query кэши useTask +
// useTaskEvents — TanStack Query автоматически re-fetch'нет данные.
//
// Ждущая подтверждения задача (awaiting_approval, а также escalated,
// вошедшая в неё прямо из ожидания) показывает MergeApprovalBanner:
// снимок гейта и кнопку подтверждения слияния. Слить PR в хостинге
// по-прежнему можно, задача перейдёт в done по событию о слиянии.
//
// Баннер эскалации вынесен в EscalatedBanner.tsx наряду с
// PlanApprovalBanner.tsx.
//
// cancel — рабочая кнопка с подтверждением (POST /tasks/{id}/cancel).
// На escalated-статусе сверху показывается баннер «Требуется ваше
// внимание» с четырьмя кнопками: rerun, cancel, «поправить руками»
// (ссылка на PR), «запустить infra-agent'а» (disabled, TODO(#314)).
//
// awaiting_plan_approval — баннер одобрения плана.
// CancelTaskButton вынесен в CancelTaskButton.tsx (переиспользуется
// в EscalatedBanner, PlanApprovalBanner и PR-секции).
// PlanApprovalBanner вынесен в PlanApprovalBanner.tsx.
//
// Чек-лист задачи: появится после реализации
// orchestrator-agent с update_task_plan.
export function TaskDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((state) => state.identity !== null);
  const modules = useEnabledModules();

  // id всегда определён внутри роута `/tasks/:id` — react-router гарантирует
  // matching до рендера TaskDetailPage. Non-null assertion подавляет
  // `Partial<{id: string}>` от useParams без fallback на "" (мёртвый branch).
  const taskId = id!;
  const taskQuery = useTask(taskId);
  const eventsQuery = useTaskEvents(taskId);

  // SSE: подписываемся отдельным стримом ради onMessage-callback'а
  // (инвалидируем react-query кэши для этой задачи). Toast'ы
  // выключены (showToast: false) — глобальный
  // <NotificationStreamSubscriber/> в ProtectedRoute их уже шлёт,
  // иначе пользователь увидел бы каждое уведомление дважды.
  // `trackStatus` не передаём — default `false`: владелец глобального
  // индикатора статуса стрима один (`NotificationStreamSubscriber`),
  // эта page-level подписка не должна затирать его на unmount.
  //
  // Без модуля уведомлений карточка сама не обновляется, обновление —
  // перезагрузкой страницы (ADR-0083, «Последствия»).
  //
  // useCallback нужен, чтобы deps useEffect внутри хука не
  // пересоздавали стрим на каждый рендер TaskDetailPage (приход
  // данных react-query триггерит ре-рендер → новая ссылка
  // onMessage → effect cleanup+restart → SSE-thrashing).
  const handleNotification = useCallback(
    (dto: NotificationDTO) => {
      if (dto.type === "task_status_changed" && dto.task_id === taskId) {
        void queryClient.invalidateQueries({ queryKey: ["task", taskId] });
        void queryClient.invalidateQueries({
          queryKey: ["task-plan", taskId],
        });
        void queryClient.invalidateQueries({
          queryKey: ["task-acceptance", taskId],
        });
        void queryClient.invalidateQueries({
          queryKey: ["task-events", taskId],
        });
        void queryClient.invalidateQueries({
          queryKey: ["merge-gate", taskId],
        });
      }
    },
    [taskId, queryClient],
  );
  useNotificationStream({
    enabled: isAuthed && modules.isEnabled("notifications"),
    showToast: false,
    onMessage: handleNotification,
  });

  return (
    <Container py="xl" size="lg">
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Anchor component={Link} to="/queue" c="dimmed">
            {t("task.backToQueue")}
          </Anchor>
        </Group>

        {taskQuery.isPending ? (
          <Group>
            <Loader size="sm" />
            <Text>{t("task.loading")}</Text>
          </Group>
        ) : null}

        {taskQuery.isError ? (
          <Alert color="red" title={t("task.errorHeading")}>
            {taskQuery.error instanceof ApiError &&
            taskQuery.error.status === HTTP_NOT_FOUND &&
            taskQuery.error.code === TASK_NOT_FOUND
              ? t("task.notFound")
              : requestErrorText(taskQuery.error, t)}
          </Alert>
        ) : null}

        {taskQuery.data ? (
          <TaskBody
            task={taskQuery.data}
            events={eventsQuery.data ?? []}
            isEventsLoading={eventsQuery.isPending}
            eventsError={eventsQuery.error}
          />
        ) : null}
      </Stack>
    </Container>
  );
}

interface TaskBodyProps {
  readonly task: NonNullable<ReturnType<typeof useTask>["data"]>;
  readonly events: TaskStatusEventResponse[];
  readonly isEventsLoading: boolean;
  readonly eventsError: Error | null;
}

function TaskBody({
  task,
  events,
  isEventsLoading,
  eventsError,
}: TaskBodyProps): ReactElement {
  const { t } = useTranslation();
  const isTerminal = task.status === "done" || task.status === "cancelled";
  const isEscalated = task.status === "escalated";
  const isPlanApproval = task.status === "awaiting_plan_approval";
  const showCancel = !isTerminal && !isEscalated && !isPlanApproval;
  const showRerun = task.status === "cancelled";
  const showActions = showCancel || showRerun || task.is_paused;
  const isEpic = task.kind === "epic";

  // `GET /plan` — только pending-план, он нужен лишь рядом с approve-
  // баннером. Чек-лист эпика в работе читается каноничной ручкой
  // `GET /acceptance`.
  const planEnabled = isPlanApproval;
  const planQuery = useTaskPlan(task.id, { enabled: planEnabled });
  const plan = planQuery.data;
  const acceptanceEnabled = isEpic && !isPlanApproval;
  const acceptanceQuery = useTaskAcceptance(task.id, {
    enabled: acceptanceEnabled,
  });

  return (
    <>
      <Title order={1}>{task.title}</Title>

      <Group gap="xs">
        <Badge size="lg" variant="filled">
          {task.status}
        </Badge>
        <Badge size="lg" variant="light">
          {task.kind}
        </Badge>
        {task.is_paused ? (
          <Badge color="yellow">{t("task.flagPaused")}</Badge>
        ) : null}
        {task.is_cancelled ? (
          <Badge color="gray">{t("task.flagCancelled")}</Badge>
        ) : null}
        {task.is_failed ? (
          <Badge color="red">{t("task.flagFailed")}</Badge>
        ) : null}
        {task.is_escalated ? (
          <Badge color="orange">{t("task.flagEscalated")}</Badge>
        ) : null}
      </Group>

      {isPlanApproval ? <PlanApprovalBanner task={task} /> : null}
      {isAwaitingMergeApproval(task, events) ? (
        <MergeApprovalBanner task={task} />
      ) : null}
      {isEscalated ? <EscalatedBanner task={task} events={events} /> : null}

      <Paper withBorder p="md">
        <Stack gap="xs">
          <Title order={3}>{t("task.descriptionTitle")}</Title>
          {task.body !== null && task.body !== "" ? (
            <Text style={{ whiteSpace: "pre-wrap" }}>{task.body}</Text>
          ) : (
            <Text c="dimmed">{t("task.descriptionEmpty")}</Text>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap={4}>
          <FieldRow label={t("task.fieldId")} value={task.id} />
          <FieldRow
            label={t("task.fieldRepository")}
            value={task.repository_full_name ?? "—"}
          />
          <FieldRow
            label={t("task.fieldIssueNumber")}
            value={
              task.issue_number !== null ? `#${String(task.issue_number)}` : "—"
            }
          />
          <FieldRow
            label={t("task.fieldHeadBranch")}
            value={task.head_branch ?? "—"}
          />
          <FieldRow label={t("task.fieldCreated")} value={task.created_at} />
          <FieldRow label={t("task.fieldUpdated")} value={task.updated_at} />
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <Title order={3}>{t("task.fieldPrUrl")}</Title>
          {task.pr_url !== null ? (
            <>
              <Group>
                <Button
                  component="a"
                  href={task.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("task.openPrButton")}
                </Button>
              </Group>
              <Text c="dimmed" size="sm">
                {t("task.openPrHint")}
              </Text>
            </>
          ) : hasUnrecordedPrAttempt(task) ? (
            <Alert color="orange" title={t("task.prAttemptUnrecordedTitle")}>
              <Stack gap="xs">
                <Text>{t("task.prAttemptUnrecordedBody")}</Text>
                <Text size="sm">
                  {t("task.prAttemptStartedAt")}: {task.pr_creation_started_at}
                </Text>
              </Stack>
            </Alert>
          ) : (
            <Text c="dimmed">{t("task.noPr")}</Text>
          )}
        </Stack>
      </Paper>

      {showActions ? (
        <Paper withBorder p="md">
          <Stack gap="sm">
            <Title order={3}>{t("task.actionsTitle")}</Title>
            {/* На ESCALATED и AWAITING_PLAN_APPROVAL cancel/rerun уже есть
              в баннере — не дублируем. */}
            {showCancel ? (
              <Group>
                <CancelTaskButton task={task} />
              </Group>
            ) : null}
            {/* CANCELLED — терминальный, кнопок cancel нет; но человек
              может вручную перезапустить отменённую задачу. */}
            {showRerun ? (
              <Group>
                <RerunTaskButton task={task} />
              </Group>
            ) : null}
            {/* Сервер снимает паузу на любом статусе (в т.ч. escalated), поэтому
              условие только по флагу, без гейта по статусу. */}
            {task.is_paused ? (
              <Group>
                <ResumeTaskButton task={task} />
              </Group>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {/* Pending-план рядом с approve-баннером: подзадачи + чек-лист +
          approval_text (то, что человек одобряет/отклоняет). */}
      {isPlanApproval && plan != null ? <PendingPlanView plan={plan} /> : null}

      {/* Эпик в работе (не awaiting): acceptance-чек-лист с прогрессом. */}
      {acceptanceEnabled && acceptanceQuery.data !== undefined ? (
        <DecompositionChecklist acceptanceItems={acceptanceQuery.data} />
      ) : null}

      {acceptanceEnabled && acceptanceQuery.isError ? (
        <Alert color="red" title={t("task.acceptanceErrorTitle")}>
          {requestErrorText(acceptanceQuery.error, t)}
        </Alert>
      ) : null}

      {planEnabled && planQuery.isError ? (
        <Alert color="red" title={t("task.planErrorTitle")}>
          {requestErrorText(planQuery.error, t)}
        </Alert>
      ) : null}

      <Paper withBorder p="md">
        <Stack gap="sm">
          <Title order={3}>{t("task.eventsTitle")}</Title>
          <EventsLog
            events={events}
            isLoading={isEventsLoading}
            error={eventsError}
          />
        </Stack>
      </Paper>
    </>
  );
}

function FieldRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): ReactElement {
  return (
    <Group justify="space-between" gap="sm">
      <Text c="dimmed" size="sm">
        {label}
      </Text>
      <Text size="sm" ff="monospace">
        {value}
      </Text>
    </Group>
  );
}

interface EventsLogProps {
  readonly events: TaskStatusEventResponse[];
  readonly isLoading: boolean;
  readonly error: Error | null;
}

function EventsLog({ events, isLoading, error }: EventsLogProps): ReactElement {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Group>
        <Loader size="xs" />
        <Text>{t("task.loading")}</Text>
      </Group>
    );
  }
  if (error !== null) {
    // Fail-loud: показываем ошибку вместо «пусто» — пользователю
    // важно понять, что лог не пуст «потому что переходов не было»,
    // а потому что backend ответил 5xx.
    return (
      <Alert color="red" title={t("task.errorHeading")}>
        {requestErrorText(error, t)}
      </Alert>
    );
  }
  if (events.length === 0) {
    return <Text c="dimmed">{t("task.eventsEmpty")}</Text>;
  }

  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("task.eventsColFrom")}</Table.Th>
          <Table.Th>{t("task.eventsColTo")}</Table.Th>
          <Table.Th>{t("task.eventsColActor")}</Table.Th>
          <Table.Th>{t("task.eventsColReason")}</Table.Th>
          <Table.Th>{t("task.eventsColAt")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((event) => (
          <Table.Tr key={event.id}>
            <Table.Td>{event.from_status ?? "—"}</Table.Td>
            <Table.Td>
              <Badge variant="light">{event.to_status}</Badge>
            </Table.Td>
            <Table.Td>
              {t(`task.eventsActor${capitalize(event.actor_kind)}`)}
            </Table.Td>
            <Table.Td>{event.reason ?? "—"}</Table.Td>
            <Table.Td>{event.at}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
